import axios, {
    AxiosError,
    AxiosInstance,
    AxiosRequestConfig,
    InternalAxiosRequestConfig,
} from 'axios';

/**
 * Public routes (no auth required). Used to avoid redirect loops on 401.
 */
const PUBLIC_ROUTES = new Set(['/login', '/register']);

/**
 * Endpoints that are commonly called during auth bootstrap.
 * If these 401, it often just means "no session yet" (not necessarily an error).
 */
const AUTH_BOOTSTRAP_PATHS = ['/auth/me', '/auth/tenant'];

// When VITE_API_URL is set (staging/prod), the API is cross-origin (API Gateway).
// When unset (local dev), API is same-origin via Vite proxy.
const API_BASE_URL = import.meta.env.VITE_API_URL
    ? `${import.meta.env.VITE_API_URL}/api/v1`
    : '/api/v1';
const IS_CROSS_ORIGIN_API = !!import.meta.env.VITE_API_URL;

const api: AxiosInstance = axios.create({
    baseURL: API_BASE_URL,
    headers: { 'Content-Type': 'application/json' },
    timeout: 30_000,
    // withCredentials requires AllowOrigins to be specific (not *).
    // For cross-origin API Gateway, cookies are not used — see Bearer token below.
    withCredentials: !IS_CROSS_ORIGIN_API,
});

/**
 * Bearer-token auth for cross-origin mode (staging/prod against API Gateway).
 *
 * In same-origin local dev the session rides on an httpOnly cookie. But the
 * Amplify frontend and the API Gateway are on different domains, and the auth
 * cookie is SameSite=Strict, so the browser never sends it cross-site — the
 * session would silently never persist. The backend also accepts
 * `Authorization: Bearer`, so cross-origin we store the JWT (returned in the
 * login response body) and attach it to every request. Persisted in
 * localStorage so a page refresh keeps the session.
 */
const TOKEN_STORAGE_KEY = 'access_token';

let authToken: string | null =
    IS_CROSS_ORIGIN_API && typeof localStorage !== 'undefined'
        ? localStorage.getItem(TOKEN_STORAGE_KEY)
        : null;

export function setAuthToken(token: string | null): void {
    authToken = token;
    if (!IS_CROSS_ORIGIN_API || typeof localStorage === 'undefined') return;
    if (token) {
        localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
}

/**
 * Read a cookie value by name (only works for non-httpOnly cookies).
 */
function getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
}

function isPublicRoute(pathname: string): boolean {
    return PUBLIC_ROUTES.has(pathname);
}

function isAuthBootstrapRequest(url?: string): boolean {
    if (!url) return false;
    return AUTH_BOOTSTRAP_PATHS.some((p) => url.includes(p));
}

function redirectToLogin() {
    if (typeof window === 'undefined') return;

    const { pathname, search } = window.location;

    // Avoid redirect loops from /login or /register
    if (isPublicRoute(pathname)) return;

    // Preserve where the user was trying to go
    const next = encodeURIComponent(pathname + (search || ''));
    window.location.href = `/login?next=${next}`;
}

// Request interceptor: attach CSRF token on state-changing requests (local dev only).
// Cross-origin API Gateway uses Cognito tokens — no cookies, no CSRF needed.
api.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        if (IS_CROSS_ORIGIN_API) {
            // Cross-origin: the cookie is never sent, so authenticate with the
            // Bearer token captured at login.
            if (authToken) {
                config.headers = config.headers ?? {};
                config.headers['Authorization'] = `Bearer ${authToken}`;
            }
        } else {
            const method = (config.method || '').toLowerCase();
            if (method !== 'get' && method !== 'head' && method !== 'options') {
                const csrfToken = getCookie('csrf_token');
                if (csrfToken) {
                    config.headers = config.headers ?? {};
                    config.headers['X-CSRF-Token'] = csrfToken;
                }
            }
        }
        return config;
    },
    (error) => Promise.reject(error)
);

/**
 * Response interceptor: handle 401 in a controlled way.
 *
 * With httpOnly cookies, JS cannot check whether a cookie exists.
 * On 401, we clear local auth state and redirect to login unless
 * we're on a public route or during bootstrap.
 */
api.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
        const status = error.response?.status;
        const requestUrl = (error.config as AxiosRequestConfig | undefined)?.url;

        if (status === 401) {
            // The session is invalid — drop any stored Bearer token so it isn't
            // re-sent on the next request (and so a stale token doesn't linger).
            setAuthToken(null);
            if (typeof window !== 'undefined') {
                const { pathname } = window.location;

                // Only redirect from protected pages, not during auth bootstrap
                if (!isPublicRoute(pathname) && !isAuthBootstrapRequest(requestUrl)) {
                    // Clear Zustand auth state without calling server logout
                    // (token is already invalid, calling logout would also 401)
                    const { useAuthStore } = require('./store');
                    useAuthStore.getState().clearLocalAuth();
                    redirectToLogin();
                }
            }
        }

        return Promise.reject(error);
    }
);

/**
 * Extract a user-friendly error message from API responses.
 *
 * Supports the structured error format: { error: { code, message } }
 * Falls back to legacy `detail` field or generic message.
 */
export function getErrorMessage(error: unknown): string {
    if (error && typeof error === 'object' && 'response' in error) {
        const axiosErr = error as AxiosError<{
            error?: { code: string; message: string };
            detail?: string;
        }>;
        const data = axiosErr.response?.data;
        if (data?.error?.message) return data.error.message;
        if (data?.detail) return data.detail;
    }
    if (error instanceof Error) return error.message;
    return 'An unexpected error occurred';
}

/**
 * Auth API
 */
export const authApi = {
    login: async (email: string, password: string) => {
        const response = await api.post('/auth/login', { email, password });
        const data = response.data as {
            access_token: string;
            token_type: string;
            expires_in: number;
        };
        // Cross-origin: store the JWT for the Bearer interceptor. No-op in
        // same-origin local dev (the httpOnly cookie carries the session).
        setAuthToken(data.access_token);
        return data;
    },

    register: async (data: {
        email: string;
        password: string;
        full_name: string;
        organization_name?: string;
        language?: string;
    }) => {
        const response = await api.post('/auth/register', data);
        return response.data;
    },

    getMe: async () => {
        const response = await api.get('/auth/me');
        return response.data;
    },

    updateProfile: async (data: {
        full_name?: string;
        phone?: string;
        language?: string;
    }) => {
        const response = await api.patch('/auth/me', data);
        return response.data;
    },

    getTenant: async () => {
        const response = await api.get('/auth/tenant');
        return response.data;
    },

    logout: async () => {
        try {
            await api.post('/auth/logout');
        } finally {
            setAuthToken(null);
        }
    },
};

/**
 * Polygons API
 */
export const polygonsApi = {
    list: async (params?: {
        page?: number;
        page_size?: number;
        commodity?: string;
        status?: string;
        search?: string;
    }) => {
        const response = await api.get('/polygons', { params });
        return response.data;
    },

    get: async (id: string) => {
        const response = await api.get(`/polygons/${id}`);
        return response.data;
    },

    create: async (data: {
        name: string;
        geometry: object;
        producer_name?: string;
        commodity?: string;
        country?: string;
        region?: string;
    }) => {
        const response = await api.post('/polygons', data);
        return response.data;
    },

    update: async (
        id: string,
        data: Partial<{
            name: string;
            producer_name: string;
            legal_name: string;
            organization: string;
            commodity: string;
            country: string;
            region: string;
            status: string;
        }>
    ) => {
        const response = await api.patch(`/polygons/${id}`, data);
        return response.data;
    },

    delete: async (id: string) => {
        await api.delete(`/polygons/${id}`);
    },

    uploadFile: async (file: File, commodity?: string) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post('/polygons/upload', formData, {
            params: { commodity },
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120_000,
        });
        return response.data;
    },

    reverseGeocode: async (lat: number, lon: number): Promise<{ country: string; region: string; country_code: string }> => {
        const response = await api.get('/polygons/reverse-geocode', { params: { lat, lon } });
        return response.data;
    },

    /** @deprecated Use uploadFile instead */
    uploadGeoJSON: async (file: File, commodity?: string) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post('/polygons/upload', formData, {
            params: { commodity },
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120_000,
        });
        return response.data;
    },

    getGeoJSON: async (params?: { commodity?: string; status?: string }) => {
        const response = await api.get('/polygons/geojson', { params });
        return response.data;
    },

    batchDelete: async (data: { polygon_ids: string[] }) => {
        const response = await api.post('/polygons/batch/delete', data);
        return response.data;
    },
};

/**
 * Analysis API
 */
export const analysisApi = {
    listJobs: async (params?: {
        page?: number;
        page_size?: number;
        status?: string;
        polygon_id?: string;
        archived?: boolean;
    }) => {
        const response = await api.get('/analysis/jobs', { params });
        return response.data;
    },

    /** Edit job metadata (label/notes) and/or archive/restore it. */
    updateJob: async (
        id: string,
        data: Partial<{ label: string; notes: string; is_archived: boolean }>
    ) => {
        const response = await api.patch(`/analysis/jobs/${id}`, data);
        return response.data;
    },

    getJob: async (id: string) => {
        const response = await api.get(`/analysis/jobs/${id}`);
        return response.data;
    },

    getJobStatus: async (id: string) => {
        const response = await api.get(`/analysis/jobs/${id}/status`);
        return response.data;
    },

    getResults: async (id: string) => {
        const response = await api.get(`/analysis/jobs/${id}/results`);
        return response.data;
    },

    createJob: async (data: {
        polygon_id: string;
        commodity?: string;
        baseline_year?: number;
        monitoring_year?: number;
        generate_report?: boolean;
        report_language?: string;
    }) => {
        const response = await api.post('/analysis/jobs', data);
        return response.data;
    },

    createBatch: async (data: {
        polygon_ids: string[];
        commodity?: string;
        baseline_year?: number;
        monitoring_year?: number;
    }) => {
        const response = await api.post('/analysis/batch', data);
        return response.data;
    },

    cancelJob: async (id: string) => {
        await api.delete(`/analysis/jobs/${id}`);
    },

    /** Cancel every not-yet-started (pending/queued) job for the tenant. */
    cancelQueued: async (): Promise<{ cancelled: number }> => {
        const response = await api.post('/analysis/jobs/cancel-queued');
        return response.data;
    },

    /**
     * Fetch an analysis imagery file with auth and return a blob URL.
     */
    getImageryBlobUrl: async (jobId: string, imageType: string): Promise<string | null> => {
        try {
            const response = await api.get(`/analysis/jobs/${jobId}/imagery/${imageType}`, {
                responseType: 'blob',
            });
            return URL.createObjectURL(response.data);
        } catch {
            return null;
        }
    },
};

/**
 * Reports API
 */
export const reportsApi = {
    list: async (params?: {
        page?: number;
        page_size?: number;
        language?: string;
        archived?: boolean;
    }) => {
        const response = await api.get('/reports', { params });
        return response.data;
    },

    get: async (id: string) => {
        const response = await api.get(`/reports/${id}`);
        return response.data;
    },

    /** Edit report metadata (label/notes) and/or archive/restore it. */
    update: async (
        id: string,
        data: Partial<{ label: string; notes: string; is_archived: boolean }>
    ) => {
        const response = await api.patch(`/reports/${id}`, data);
        return response.data;
    },

    getSnapshot: async (id: string) => {
        const response = await api.get(`/reports/${id}/snapshot`);
        return response.data;
    },

    getDownloadUrl: async (id: string) => {
        const response = await api.get(`/reports/${id}/download`);
        return response.data;
    },

    download: async (id: string, filename: string) => {
        // Download PDF with authentication
        const response = await api.get(`/reports/${id}/file`, {
            responseType: 'blob', // Important: get binary data
        });

        // Create blob URL and trigger download
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    },
};

/**
 * Commodities API
 */
export const commoditiesApi = {
    list: async () => {
        const response = await api.get('/commodities');
        return response.data;
    },
};

/**
 * Validation API
 */
export const validationApi = {
    validatePolygon: async (
        polygonId: string,
        options?: {
            include_baseline?: boolean;
            include_fao_thresholds?: boolean;
            include_edge_effects?: boolean;
            buffer_distance_m?: number;
        }
    ) => {
        const response = await api.post(
            `/validation/polygons/${polygonId}`,
            options || {}
        );
        return response.data;
    },

    quickValidate: async (polygonId: string) => {
        const response = await api.get(`/validation/polygons/${polygonId}/quick`);
        return response.data;
    },

    batchValidate: async (data: {
        polygon_ids: string[];
        validation_options?: {
            include_baseline?: boolean;
            include_fao_thresholds?: boolean;
            include_edge_effects?: boolean;
            buffer_distance_m?: number;
        };
    }) => {
        const response = await api.post('/validation/polygons/batch', data);
        return response.data;
    },
};

/**
 * Dashboard API
 */
export const dashboardApi = {
    getStats: async () => {
        const response = await api.get('/dashboard/stats');
        return response.data;
    },
};

/**
 * Export API
 */
export const exportApi = {
    downloadPolygons: async (
        format: 'geojson' | 'shapefile' | 'kml' | 'gpkg',
        options?: { polygon_ids?: string; commodity?: string }
    ) => {
        const response = await api.get('/export/polygons', {
            params: { format, ...options },
            responseType: 'blob',
            timeout: 60_000,
        });

        const extensions: Record<string, string> = {
            geojson: '.geojson',
            shapefile: '.zip',
            kml: '.kml',
            gpkg: '.gpkg',
        };

        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `polygons_export${extensions[format] || ''}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    },
};

/**
 * Analytics API — portfolio-level intelligence (see app/api/v1/analytics.py).
 * `filters` are the shared query params: period, commodity, country, status, risk.
 */
type AnalyticsParams = Record<string, string | number | undefined>;

export const analyticsApi = {
    summary: async (filters: AnalyticsParams) => {
        const response = await api.get('/analytics/summary', { params: filters });
        return response.data;
    },
    timeseries: async (metric: string, filters: AnalyticsParams) => {
        const response = await api.get('/analytics/timeseries', { params: { metric, ...filters } });
        return response.data;
    },
    breakdown: async (dimension: string, filters: AnalyticsParams) => {
        const response = await api.get('/analytics/breakdown', { params: { dimension, ...filters } });
        return response.data;
    },
    leaderboard: async (metric: string, filters: AnalyticsParams) => {
        const response = await api.get('/analytics/leaderboard', { params: { metric, ...filters } });
        return response.data;
    },
    polygons: async (filters: AnalyticsParams) => {
        const response = await api.get('/analytics/polygons', { params: filters });
        return response.data;
    },
};

/**
 * Spatial Analysis API
 */
export const spatialApi = {
    detectOverlaps: async (data?: {
        polygon_ids?: string[];
        commodity?: string;
    }) => {
        const response = await api.post('/spatial/overlaps', data || {});
        return response.data;
    },

    detectSingleOverlaps: async (polygonId: string) => {
        const response = await api.get(`/spatial/overlaps/${polygonId}`);
        return response.data;
    },

    bufferPolygon: async (
        polygonId: string,
        data: { distance_m: number; save_as_new?: boolean; new_name?: string }
    ) => {
        const response = await api.post(`/spatial/buffer/${polygonId}`, data);
        return response.data;
    },

    simplifyPolygon: async (
        polygonId: string,
        data: { tolerance_m: number; save_as_new?: boolean; new_name?: string }
    ) => {
        const response = await api.post(`/spatial/simplify/${polygonId}`, data);
        return response.data;
    },

    findNearby: async (
        polygonId: string,
        data: { max_distance_m: number }
    ) => {
        const response = await api.post(`/spatial/proximity/${polygonId}`, data);
        return response.data;
    },

    updateGeometry: async (
        polygonId: string,
        data: { geometry: object; reason?: string }
    ) => {
        const response = await api.put(`/spatial/geometry/${polygonId}`, data);
        return response.data;
    },

    checkContainment: async (data: {
        outer_polygon_id: string;
        inner_polygon_id: string;
    }) => {
        const response = await api.post('/spatial/containment', data);
        return response.data;
    },

    getSpatialStats: async (polygonId: string) => {
        const response = await api.get(`/spatial/stats/${polygonId}`);
        return response.data;
    },

    // Batch operations (on /polygons/batch/* endpoints)
    batchDelete: async (data: { polygon_ids: string[] }) => {
        const response = await api.post('/polygons/batch/delete', data);
        return response.data;
    },

    batchTag: async (data: { polygon_ids: string[]; tags: string[]; action: string }) => {
        const response = await api.post('/polygons/batch/tag', data);
        return response.data;
    },

    batchUpdate: async (data: {
        polygon_ids: string[];
        commodity?: string;
        country?: string;
        region?: string;
        status?: string;
    }) => {
        const response = await api.post('/polygons/batch/update', data);
        return response.data;
    },
};

export default api;
